import { getGlobal } from '../global';

import { selectIsCurrentUserPremium } from '../global/selectors';
import { callApi } from '../api/gramjs';

// `normal` = SpamBot confirmed there are no limits, `connected` = online but the check gave no answer
export type SeatAccountStatus = 'normal' | 'connected' | 'frozen' | 'appeal_required' | 'limited_temp';

export type SeatCheckResult = {
  isVip: boolean;
  loginStatus: SeatAccountStatus;
  errorMessage?: string;
};

// Same wording set the server-side forwarder matches in `_detect_spambot_status`
const FROZEN_MARKERS = ['blocked', 'violations', '冻结', 'заблокирован', 'нарушени'];
// An appeal is only needed when the limit has no automatic release date, otherwise it is temporary
const LIMITED_MARKERS = ['limited', 'restricted', 'cannot send messages', 'anti-spam', 'ограничен'];
const APPEAL_MARKERS = ['submit a complaint', 'limited by mistake', 'if you think', 'ошибочно', 'обжаловать'];
const RELEASE_MARKERS = [
  'limited until', 'automatically released', 'will be automatically', 'will be lifted', 'автоматически',
];
const NO_LIMITS_MARKERS = [
  'no limits', 'no restrictions', 'good news', 'buenas noticias', 'no existen limitaciones',
  'свободен от', 'ограничений', 'gute nachrichten', 'keine einschränkungen',
];
const RESTRICTED_UNTIL_REGEX = /(?:until|bis|hasta|до)\s+(.+?UTC)/i;
const DATE_PREFIX_REGEX = /^(?:el|den|the)\s+/i;

// Fallbacks the server-side forwarder uses when SpamBot gives no usable answer
export const COOLDOWN_WHEN_SPAM_BOT_SEES_NO_LIMIT = 3600;
export const COOLDOWN_WHEN_SPAM_BOT_UNKNOWN = 7200;

// Runs once the seat account is online: reads Premium from the account itself and asks SpamBot
// about restrictions, so forwarding can later rely on the stored values instead of rechecking
export async function runSeatSelfCheck(): Promise<SeatCheckResult> {
  // Premium comes from Telegram itself, not from the cached user the session was restored with,
  // so the fresh profile has to be in before it is read, even when SpamBot fails right away.
  // A failed refresh only means the cached Premium flag is used, it must not fail the whole check
  await callApi('fetchCurrentUser').catch(() => undefined);

  let reply: string | undefined;
  let checkError: string | undefined;
  try {
    reply = await callApi('fetchSeatSpamBotReply');
  } catch (err) {
    checkError = err instanceof Error ? err.message : String(err);
  }

  const isVip = selectIsCurrentUserPremium(getGlobal());

  if (checkError) return { isVip, loginStatus: 'connected', errorMessage: checkError };

  if (!reply) return { isVip, loginStatus: 'connected' };

  const abnormalStatus = detectSpamBotStatus(reply);
  if (abnormalStatus) {
    return { isVip, loginStatus: abnormalStatus, errorMessage: reply.slice(0, 300) };
  }

  const cooldown = parseSpamBotCooldown(reply);
  if (cooldown && cooldown > 0) {
    return { isVip, loginStatus: 'limited_temp', errorMessage: reply.slice(0, 300) };
  }

  return { isVip, loginStatus: 'normal' };
}

// Asks SpamBot for the real cooldown of the current account, mirroring `get_spambot_cooldown`:
// `frozen` / `appeal_required` when the account itself is in trouble (no cooldown is recorded for
// those, the status is written instead), otherwise the remaining seconds (0 = no restrictions)
export async function fetchSeatCooldownSeconds(): Promise<
number | 'frozen' | 'appeal_required' | undefined
> {
  const reply = await callApi('fetchSeatSpamBotReply');
  if (!reply) return undefined;

  const abnormalStatus = detectSpamBotStatus(reply);
  if (abnormalStatus) return abnormalStatus;

  const lowered = reply.toLowerCase();
  if (NO_LIMITS_MARKERS.some((marker) => lowered.includes(marker) || reply.includes(marker))) return 0;

  return parseSpamBotCooldown(reply);
}

function detectSpamBotStatus(reply: string): 'frozen' | 'appeal_required' | undefined {
  const lowered = reply.toLowerCase();
  if (FROZEN_MARKERS.some((marker) => lowered.includes(marker) || reply.includes(marker))) return 'frozen';

  const isLimited = LIMITED_MARKERS.some((marker) => lowered.includes(marker) || reply.includes(marker));
  const hasAppeal = APPEAL_MARKERS.some((marker) => lowered.includes(marker) || reply.includes(marker));
  const hasRelease = RELEASE_MARKERS.some((marker) => lowered.includes(marker) || reply.includes(marker));
  if (isLimited && hasAppeal && !hasRelease) return 'appeal_required';

  return undefined;
}

// SpamBot reports limits as `... until 5 Apr 2026, 14:09 UTC`
function parseSpamBotCooldown(reply: string) {
  const match = reply.match(RESTRICTED_UNTIL_REGEX);
  if (!match) return undefined;

  const dateString = match[1].trim().replace(DATE_PREFIX_REGEX, '');
  const limitTime = Date.parse(`${dateString.replace(' UTC', '')} UTC`);
  if (Number.isNaN(limitTime)) return undefined;

  const seconds = Math.floor((limitTime - Date.now()) / 1000);
  return seconds > 0 ? seconds : 0;
}
